import React from "react";
import { VenueBookmark } from "@iventis/domain-model/model/venueBookmark";
import {
    Body2,
    borderRadius,
    formGap,
    Header4,
    InteractiveElement,
    narrowPadding,
    popoverPadding,
    popoverShadow,
    skeletonShimmer,
    styled,
    StyledIconButton,
    zIndexes,
} from "@iventis/styles";
import { Theme } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingComponent, SimpleTooltip } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { useQuery } from "@tanstack/react-query";

interface VenueBookmarkOperations {
    onVenueBookmarkSelect: (bookmark: VenueBookmark) => void;
    onVenueBookmarkDelete: (bookmarkId: string) => Promise<void>;
    onVenueBookmarkCreate: () => void;
}

export interface VenueBookmarksViewProps {
    venueBookmarks: VenueBookmark[];
    venueBookmarkOperations: VenueBookmarkOperations;
    getAssetUrl: (assetId: string) => Promise<string>;
    className?: string;
    loading: boolean;
    onClose: () => void;
}

export const VenueBookmarksView: React.FC<VenueBookmarksViewProps> = ({ venueBookmarks, venueBookmarkOperations, getAssetUrl, className, loading, onClose }) => {
    const translate = useIventisTranslate();

    return (
        <StyledBookmarkViewContainer className={className}>
            <StyledHeaderContainer>
                <Header4>{translate(Content.map10.venue_bookmark_view.bookmarks)}</Header4>
                <SimpleTooltip text={translate(Content.common.buttons.close)} placement="top">
                    <StyledIconButton onClick={onClose}>
                        <FontAwesomeIcon icon={["far", "times"]} size="xs" />
                    </StyledIconButton>
                </SimpleTooltip>
            </StyledHeaderContainer>
            <StyledHorizontalRule />
            <StyledBookmarksContainer>
                {loading ? (
                    <LoadingComponentContainer>
                        <LoadingComponent />
                    </LoadingComponentContainer>
                ) : (
                    <>
                        {venueBookmarks.map((bookmark) => (
                            <Bookmark
                                key={bookmark.id}
                                bookmark={bookmark}
                                onDelete={async (id) => {
                                    await venueBookmarkOperations.onVenueBookmarkDelete(id);
                                }}
                                onSelect={venueBookmarkOperations.onVenueBookmarkSelect}
                                getAssetUrl={getAssetUrl}
                            />
                        ))}
                        {venueBookmarks.length === 0 && <Body2>{translate(Content.map10.venue_bookmark_view.no_bookmarks)}</Body2>}
                    </>
                )}
            </StyledBookmarksContainer>

            <SimpleTooltip text={translate(Content.map10.venue_bookmark_view.create_bookmark)} placement="top">
                <FloatingAddBookmarkButton onClick={venueBookmarkOperations.onVenueBookmarkCreate}>
                    <AddIcon size="xs" icon={["far", "plus"]} />
                </FloatingAddBookmarkButton>
            </SimpleTooltip>
        </StyledBookmarkViewContainer>
    );
};

const LoadingComponentContainer = styled.div`
    overflow-y: hidden;
`;

const FloatingAddBookmarkButton = styled(StyledIconButton)`
    position: absolute;
    bottom: ${popoverPadding.standard};
    right: ${popoverPadding.standard};

    width: 35px;
    height: 35px;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    &:hover {
        background-color: ${({ theme }: { theme: Theme }) => theme.primaryColors.focus};
    }
`;

const AddIcon = styled(FontAwesomeIcon)`
    color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};
    display: block;
    margin: 0;
`;

const StyledHorizontalRule = styled.hr`
    background-color: ${({ theme }: { theme: Theme }) => theme.shades.three};
    height: 0.5px;
`;

const StyledHeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    background: ${(props: { theme: Theme }) => props.theme.secondaryColors.blank};
    z-index: 1;
`;

const StyledBookmarksContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${formGap};
    overflow-y: scroll;
`;

const StyledBookmarkViewContainer = styled.div`
    box-sizing: border-box;
    padding: ${popoverPadding.standard};
    background: ${(props: { theme: Theme }) => props.theme.secondaryColors.blank};
    border-radius: ${borderRadius.standard};
    box-shadow: ${popoverShadow};
    display: flex;
    flex-direction: column;
    gap: ${formGap};
    z-index: ${zIndexes.routePlanner};
    height: 100%;
`;

const Bookmark: React.FC<{
    bookmark: VenueBookmark;
    onDelete: (bookmarkId: string) => void;
    onSelect: (bookmark: VenueBookmark) => void;
    getAssetUrl: (assetId: string) => Promise<string>;
}> = ({ bookmark, onDelete, onSelect, getAssetUrl }) => {
    const translate = useIventisTranslate();

    const { data: assetUrl } = useQuery(["assetUrl", bookmark.id], async () => getAssetUrl(bookmark.thumbnailAssetId));

    return (
        <StyledInteractiveElement onClick={() => onSelect(bookmark)}>
            <StyledImageNameContainer>
                {/* If assetUrl, show image, else show skeleton */}
                {assetUrl ? <StyledBookmarkImage src={assetUrl} /> : <StyleBookmarkImageSkeleton />}
                <StyledBookmarkName>{bookmark.name}</StyledBookmarkName>
            </StyledImageNameContainer>
            <SimpleTooltip text={translate(Content.map10.venue_bookmark_view.delete_bookmark)} placement="top">
                <TrashIconButton
                    onClick={(event) => {
                        onDelete(bookmark.id);
                        event.stopPropagation();
                    }}
                    className="bookmark-trash"
                >
                    <FontAwesomeIcon icon={["far", "trash"]} size="xs" />
                </TrashIconButton>
            </SimpleTooltip>
        </StyledInteractiveElement>
    );
};

const TrashIconButton = styled(StyledIconButton)`
    align-self: center;
`;

const StyledInteractiveElement = styled(InteractiveElement)`
    display: flex;
    align-items: center;
    padding: ${narrowPadding};
    justify-content: space-between;
    // Hide trash can if not hovering
    .bookmark-trash svg {
        display: none;
    }

    &:hover {
        background: ${(props: { theme: Theme }) => props.theme.secondaryColors.lightGrey};
        .bookmark-trash svg {
            display: flex;
        }
    }
`;

const StyledImageNameContainer = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    gap: ${narrowPadding};
`;

const StyledBookmarkImage = styled.img`
    width: 40%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
`;

const StyleBookmarkImageSkeleton = styled.div`
    ${skeletonShimmer}
    width: 40%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
`;

const StyledBookmarkName = styled(Body2)`
    padding-left: ${narrowPadding};
`;
